import {
	type AreaUnit,
	DEFAULT_SITE_UNITS,
	type DistanceUnit,
	type SiteUnitSettings,
	type VolumeUnit,
	areaUnits,
	distanceUnits,
	volumeUnits,
} from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import { UnitConverter } from "@earthbrain/sc-earthworks";
import { type ComputedRef, computed } from "vue";
import { useStore } from "vuex";

export interface UnitDisplayResult {
	value: number;
	label: string;
	formatted: string;
}

export interface UnitDisplayContext {
	distanceLabel: ComputedRef<string>;
	areaLabel: ComputedRef<string>;
	volumeLabel: ComputedRef<string>;
	distanceUnitNameKey: ComputedRef<string>;
	areaUnitNameKey: ComputedRef<string>;
	volumeUnitNameKey: ComputedRef<string>;
	toDisplayDistance: (
		internalM: number,
		decimals?: number,
	) => UnitDisplayResult;
	fromDisplayDistance: (displayValue: number) => number;
	toDisplayCoordinates: (
		internalCoords: [number, number],
		decimals?: number,
	) => { x: UnitDisplayResult; y: UnitDisplayResult };
	fromDisplayCoordinates: (
		displayX: number,
		displayY: number,
	) => [number, number];
	toDisplayArea: (internalM2: number, decimals?: number) => UnitDisplayResult;
	toDisplayVolume: (internalM3: number, decimals?: number) => UnitDisplayResult;
	siteUnits: ComputedRef<SiteUnitSettings>;
}

function getDistanceLabel(unit: DistanceUnit): string {
	switch (unit) {
		case distanceUnits.METERS:
			return "m";
		case distanceUnits.FEET:
			return "ft";
		case distanceUnits.US_SURVEY_FEET:
			return "ft";
		default: {
			const _: never = unit;
			return _;
		}
	}
}

function getAreaLabel(unit: AreaUnit): string {
	switch (unit) {
		case areaUnits.SQUARE_METERS:
			return "m²";
		case areaUnits.SQUARE_FEET:
			return "sq ft";
		case areaUnits.ACRES:
			return "ac";
		case areaUnits.HECTARES:
			return "ha";
		default: {
			const _: never = unit;
			return _;
		}
	}
}

function getVolumeLabel(unit: VolumeUnit): string {
	switch (unit) {
		case volumeUnits.CUBIC_METERS:
			return "m³";
		case volumeUnits.CUBIC_YARDS:
			return "cu yd";
		default: {
			const _: never = unit;
			return _;
		}
	}
}

function getDistanceUnitNameKey(unit: DistanceUnit): string {
	switch (unit) {
		case distanceUnits.METERS:
			return "METERS_WORD";
		case distanceUnits.FEET:
			return "FEET_WORD";
		case distanceUnits.US_SURVEY_FEET:
			return "US_SURVEY_FEET_WORD";
		default: {
			const _: never = unit;
			return _;
		}
	}
}

function getAreaUnitNameKey(unit: AreaUnit): string {
	switch (unit) {
		case areaUnits.SQUARE_METERS:
			return "SQUARE_METERS_WORD";
		case areaUnits.SQUARE_FEET:
			return "SQUARE_FEET_WORD";
		case areaUnits.ACRES:
			return "ACRES_WORD";
		case areaUnits.HECTARES:
			return "HECTARES_WORD";
		default: {
			const _: never = unit;
			return _;
		}
	}
}

function getVolumeUnitNameKey(unit: VolumeUnit): string {
	switch (unit) {
		case volumeUnits.CUBIC_METERS:
			return "METERS_CUBED_WORD";
		case volumeUnits.CUBIC_YARDS:
			return "CUBIC_YARDS_WORD";
		default: {
			const _: never = unit;
			return _;
		}
	}
}

function createDisplayResult(
	value: number,
	label: string,
	decimals?: number,
): UnitDisplayResult {
	const numericValue = Number(value) || 0;
	const roundedValue =
		decimals !== undefined
			? Number(numericValue.toFixed(decimals))
			: numericValue;
	return {
		value: roundedValue,
		label,
		formatted: `${roundedValue} ${label}`,
	};
}

/**
 * Vue-aware wrapper over UnitConverter: converts between internal SI values
 * (m / m² / m³) and the current site's display units, with labels.
 */
export function useUnitDisplay(): UnitDisplayContext {
	const store = useStore();

	/** Falls back to metric defaults until the site is loaded. */
	const siteUnits = computed<SiteUnitSettings>(() => {
		return store.state.site?.units ?? DEFAULT_SITE_UNITS;
	});

	const distanceLabel = computed(() =>
		getDistanceLabel(siteUnits.value.distance),
	);
	const distanceUnitNameKey = computed(() =>
		getDistanceUnitNameKey(siteUnits.value.distance),
	);

	const areaLabel = computed(() => getAreaLabel(siteUnits.value.area));
	const areaUnitNameKey = computed(() =>
		getAreaUnitNameKey(siteUnits.value.area),
	);

	const volumeLabel = computed(() => getVolumeLabel(siteUnits.value.volume));
	const volumeUnitNameKey = computed(() =>
		getVolumeUnitNameKey(siteUnits.value.volume),
	);

	const distanceUnitPort = computed(() =>
		createDistanceUnitPort(siteUnits.value.distance),
	);

	function toDisplayDistance(
		internalM: number,
		decimals?: number,
	): UnitDisplayResult {
		const displayValue = distanceUnitPort.value.toDisplay(internalM);
		return createDisplayResult(displayValue, distanceLabel.value, decimals);
	}

	function fromDisplayDistance(displayValue: number): number {
		return distanceUnitPort.value.toMeters(displayValue);
	}

	function toDisplayCoordinates(
		internalCoords: [number, number],
		decimals?: number,
	): { x: UnitDisplayResult; y: UnitDisplayResult } {
		const [internalX, internalY] = internalCoords;
		const displayX = distanceUnitPort.value.toDisplay(internalX);
		const displayY = distanceUnitPort.value.toDisplay(internalY);
		return {
			x: createDisplayResult(displayX, distanceLabel.value, decimals),
			y: createDisplayResult(displayY, distanceLabel.value, decimals),
		};
	}

	function fromDisplayCoordinates(
		displayX: number,
		displayY: number,
	): [number, number] {
		const internalX = distanceUnitPort.value.toMeters(displayX);
		const internalY = distanceUnitPort.value.toMeters(displayY);
		return [internalX, internalY];
	}

	function toDisplayArea(
		internalM2: number,
		decimals?: number,
	): UnitDisplayResult {
		const displayValue = UnitConverter.internalAreaToDisplayArea(
			internalM2,
			siteUnits.value.area,
		);
		return createDisplayResult(displayValue, areaLabel.value, decimals);
	}

	function toDisplayVolume(
		internalM3: number,
		decimals?: number,
	): UnitDisplayResult {
		const displayValue = UnitConverter.internalVolumeToDisplayVolume(
			internalM3,
			siteUnits.value.volume,
		);
		return createDisplayResult(displayValue, volumeLabel.value, decimals);
	}

	return {
		distanceLabel,
		areaLabel,
		volumeLabel,
		distanceUnitNameKey,
		areaUnitNameKey,
		volumeUnitNameKey,
		toDisplayDistance,
		fromDisplayDistance,
		toDisplayCoordinates,
		fromDisplayCoordinates,
		toDisplayArea,
		toDisplayVolume,
		siteUnits,
	};
}
